/**
 * School Data Normalizer
 * 
 * Utility to normalize school data from the new v2 API format (UUID-based)
 * to be compatible with existing frontend code that expects the old format.
 */

/**
 * Normalize a single school object from v2 API format to legacy format
 * @param {Object} schoolV2 - School data from v2 API
 * @returns {Object} Normalized school data compatible with existing frontend
 */
export const normalizeSchoolData = (schoolV2) => {
  if (!schoolV2) return null;


  const result = {
    // Primary identifiers
    uuid: schoolV2.uuid,
    ncessch: schoolV2.ncessch,
    full_ncessch: schoolV2.full_ncessch,
    state_school_id: schoolV2.state_school_id,
    
    // Basic info (mapped to legacy field names)
    name: schoolV2.name || schoolV2.school_name, // Handle both field names from API
    lea_name: schoolV2.lea_name,
    
    // Location data
    latitude: schoolV2.latitude,
    longitude: schoolV2.longitude,
    city: extractCityFromStateInfo(schoolV2),
    county: schoolV2.county,
    state: schoolV2.state_abbr, // Map state_abbr to state for compatibility
    state_name: schoolV2.state_name,
    state_abbr: schoolV2.state_abbr,
    
    // School characteristics
    grade_range: schoolV2.grade_range,
    grades_offered: schoolV2.grades_offered,
    school_type: schoolV2.school_type,
    level: mapSchoolTypeToLevel(schoolV2.school_type),
    
    // Charter/governance (mapped to legacy format)
    charter: schoolV2.is_charter ? 'Yes' : 'No', // Convert boolean to string for compatibility
    is_charter: schoolV2.is_charter,
    is_private: schoolV2.is_private,
    
    // Additional v2 fields
    teachers: schoolV2.teachers,
    status: schoolV2.status,

    // Backend-authoritative drop-a-pin flag (no NCES ID AND no enrollment records)
    is_drop_a_pin: schoolV2.is_drop_a_pin,
    
    // Metadata
    data_source: 'v2_unified'
  };


  return result;
};

/**
 * Normalize an array of schools from v2 API format
 * @param {Array} schoolsV2 - Array of school data from v2 API
 * @returns {Array} Array of normalized school data
 */
export const normalizeSchoolsArray = (schoolsV2) => {
  if (!Array.isArray(schoolsV2)) return [];
  return schoolsV2.map(normalizeSchoolData).filter(Boolean);
};

/**
 * Extract city information from v2 school data
 * @param {Object} schoolV2 - School data from v2 API
 * @returns {string} City name
 */
const extractCityFromStateInfo = (schoolV2) => {
  // If we have city directly, use it
  if (schoolV2.city) return schoolV2.city;
  
  // Return empty string if no city available
  return '';
};

/**
 * Map v2 school type to legacy level format
 * @param {string} schoolType - School type from v2 API
 * @returns {string} Legacy level format
 */
const mapSchoolTypeToLevel = (schoolType) => {
  if (!schoolType) return '';
  
  const typeMapping = {
    'elementary': 'Elementary',
    'middle': 'Middle',
    'high': 'High',
    'combined': 'Combined',
    'other': 'Other'
  };
  
  return typeMapping[schoolType.toLowerCase()] || schoolType;
};

/**
 * Check if school data is from v2 API
 * @param {Object} school - School data object
 * @returns {boolean} True if from v2 API
 */
export const isV2SchoolData = (school) => {
  return school && (school.uuid || school.data_source === 'v2_unified');
};

/**
 * Get the primary identifier for a school (UUID for v2, NCESSCH for legacy)
 * @param {Object} school - School data object
 * @returns {string} Primary identifier
 */
export const getSchoolPrimaryId = (school) => {
  if (isV2SchoolData(school)) {
    return school.uuid || school.ncessch;
  }
  return school.ncessch;
};

/**
 * Normalize API response from v2 schools endpoint
 * @param {Object} response - API response from v2/schools
 * @returns {Object} Normalized response compatible with existing code
 */
export const normalizeSchoolsApiResponse = (response) => {
  if (!response) return null;
  
  if (Array.isArray(response)) {
    // Non-paginated response
    return normalizeSchoolsArray(response);
  }
  
  if (response.schools && Array.isArray(response.schools)) {
    // Paginated response
    return {
      ...response,
      schools: normalizeSchoolsArray(response.schools)
    };
  }
  
  return response;
}; 